import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const WHY_US = [
  { icon: 'fa-medal', title: 'Academic Excellence', desc: 'Rigorous curriculum and expert teaching methods for outstanding results.', color: 'bg-primary/10 text-primary' },
  { icon: 'fa-heart', title: 'Character Development', desc: 'Building strong values, moral foundation and personal integrity.', color: 'bg-accent/10 text-accent' },
  { icon: 'fa-building', title: 'Modern Facilities', desc: 'State-of-the-art learning environment with modern labs and library.', color: 'bg-green-100 text-green-600' },
  { icon: 'fa-chalkboard-teacher', title: 'Expert Faculty', desc: 'Experienced and dedicated teachers committed to student success.', color: 'bg-purple-100 text-purple-600' },
  { icon: 'fa-users', title: 'Holistic Growth', desc: 'Sports, arts, and co-curricular activities for all-round development.', color: 'bg-amber-100 text-amber-600' },
  { icon: 'fa-mosque', title: 'Islamic Values', desc: 'Strong emphasis on Islamic education, Nazra and Fehm-ul-Quran.', color: 'bg-teal-100 text-teal-600' },
];

const STEPS = [
  { num: '01', icon: 'fa-file-alt', title: 'Application', desc: 'Submit the application form with required documents and previous academic records.', color: 'bg-primary text-white' },
  { num: '02', icon: 'fa-clipboard-check', title: 'Assessment', desc: 'Student assessment test and interview with school administration.', color: 'bg-accent text-white' },
  { num: '03', icon: 'fa-check-circle', title: 'Approval', desc: 'Admission approval notification and fee structure discussion.', color: 'bg-green-600 text-white' },
  { num: '04', icon: 'fa-graduation-cap', title: 'Enrollment', desc: 'Complete enrollment formalities and begin your academic journey.', color: 'bg-purple-600 text-white' },
];

const CAMPUSES = [
  { name: 'Par Hoti Campus', badge: 'Main Campus', location: 'Nehr Chowk, Swabi Road Mardan', phone: '0343-0001144/55/66/77', programs: 'All Grades — Nursery to Grade 8', badgeColor: 'bg-primary', isMain: true },
  { name: 'DHA Campus', badge: '27+ Years', location: 'Muqam Chowk, Mardan', phone: '0343-0001133', programs: 'Play Group to Grade 5', badgeColor: 'bg-green-600', isMain: false },
  { name: 'SMT Campus', badge: 'Modern', location: 'Phase-2 Sector N, House No.1', phone: '0343-0001122', programs: 'All Grades Available', badgeColor: 'bg-purple-600', isMain: false },
];

const Admission = () => (
  <div className="page-enter">
    <Helmet>
      <title>Admission — PAK School &amp; College System | Apply Now</title>
      <meta name="description" content="Apply for admission to PAK School & College System Mardan. Join our community of excellence. Quality education from nursery to college level. Every Student A Star." />
      <meta name="keywords" content="School Admission Mardan, PAK School Apply, School Enrollment, Admission Process, Quality Education Pakistan" />
      <link rel="canonical" href="https://pakschoolandcollege.com/admission" />
    </Helmet>

    {/* Hero */}
    <div className="page-hero">
      <div className="page-hero-content">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
          🎓 Start Your Journey
        </div>
        <h1><span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Admissions</span> Open</h1>
        <p>Join our community of excellence and start your journey to success. Enrollments open for all grades.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <a href="tel:03430001144" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-poppins font-600 text-sm rounded-full hover:bg-primary-dark transition-all hover:-translate-y-0.5">
            <i className="fas fa-phone" /> Call to Enroll
          </a>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-poppins font-600 text-sm rounded-full border border-white/30 hover:bg-white/20 transition-all hover:-translate-y-0.5">
            <i className="fas fa-map-marker-alt" /> Visit Campus
          </Link>
        </div>
      </div>
    </div>

    {/* Why Choose Us */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-badge mx-auto w-fit">Why Choose Us</div>
          <h2 className="section-title">Why PAK School &amp; College System?</h2>
          <p className="section-subtitle mx-auto">We believe every student has the potential to shine. Our comprehensive approach combines academic excellence with character building.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_US.map((item, i) => (
            <div key={i} className="group flex items-start gap-4 p-5 bg-gray-50 rounded-2xl hover:bg-white hover:shadow-lg border border-transparent hover:border-gray-100 transition-all duration-300">
              <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`fas ${item.icon} text-lg`} />
              </div>
              <div>
                <h3 className="font-poppins font-700 text-navy text-base mb-1.5">{item.title}</h3>
                <p className="text-gray-500 font-inter text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Admission Process */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-badge mx-auto w-fit">Simple Steps</div>
          <h2 className="section-title">Admission Process</h2>
          <p className="section-subtitle mx-auto">Four simple steps to join our educational family</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((step, i) => (
            <div key={i} className="group relative">
              {i < STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-full w-full h-px bg-gradient-to-r from-gray-200 to-transparent -translate-x-5 z-0" />
              )}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-center relative z-10">
                <div className={`w-16 h-16 rounded-2xl ${step.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <i className={`fas ${step.icon} text-xl`} />
                </div>
                <div className="font-poppins font-800 text-gray-200 text-3xl absolute top-4 right-5">{step.num}</div>
                <h3 className="font-poppins font-700 text-navy text-base mb-2">{step.title}</h3>
                <p className="text-gray-500 font-inter text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Campuses */}
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-badge mx-auto w-fit">Our Locations</div>
          <h2 className="section-title">Our Campuses</h2>
          <p className="section-subtitle mx-auto">Choose the campus that's right for your child</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {CAMPUSES.map((campus, i) => (
            <div key={i} className={`bg-white rounded-2xl p-7 shadow-sm border transition-all duration-300 hover:shadow-xl hover:-translate-y-1.5
              ${campus.isMain ? 'border-primary/30 ring-1 ring-primary/20' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center">
                  <i className="fas fa-building text-gray-400 text-lg" />
                </div>
                <span className={`${campus.badgeColor} text-white text-xs font-poppins font-700 px-3 py-1.5 rounded-full`}>
                  {campus.badge}
                </span>
              </div>
              <h3 className="font-poppins font-700 text-navy text-lg mb-4">{campus.name}</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="fas fa-map-marker-alt text-primary mt-0.5 w-4 text-center flex-shrink-0" />
                  <p className="text-gray-500 font-inter text-sm">{campus.location}</p>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-phone text-accent w-4 text-center flex-shrink-0" />
                  <a href={`tel:${campus.phone.replace(/\D/g, '').substring(0, 11)}`} className="text-gray-500 font-inter text-sm hover:text-accent transition-colors">{campus.phone}</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-graduation-cap text-green-500 w-4 text-center flex-shrink-0" />
                  <p className="text-gray-500 font-inter text-sm">{campus.programs}</p>
                </div>
              </div>
              {campus.isMain && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs text-primary font-poppins font-600">
                    <i className="fas fa-star text-[10px]" /> Main Campus — All Programs Available
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="bg-gradient-to-br from-navy to-blue-900 rounded-3xl p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/5 -translate-y-12 translate-x-12" />
          <div className="relative">
            <div className="text-4xl mb-4">🎓</div>
            <h2 className="font-poppins font-800 text-white text-2xl sm:text-3xl mb-4">Ready to Join PAK School?</h2>
            <p className="text-white/70 font-inter text-base mb-8 max-w-lg mx-auto leading-relaxed">
              Contact us today to learn more about our admission process and schedule a campus visit. Our team is here to help every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:03430001144" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-navy font-poppins font-700 text-sm rounded-full hover:bg-gray-100 transition-all hover:-translate-y-0.5 shadow-lg">
                <i className="fas fa-phone text-primary" /> Call: 0343-0001144
              </a>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 text-white font-poppins font-600 text-sm rounded-full border border-white/30 hover:bg-white/20 transition-all hover:-translate-y-0.5">
                <i className="fas fa-envelope" /> Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default Admission;
